import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LayoutWithChat from './LayoutWithChat';
import ResponsiveTable, { Column } from './ResponsiveTable';

import { fetchPlayerDetail, PlayerInfo, PlayerSeasonStats, PlayerProjections, Transaction } from '../api/players';



interface PlayerDetailData {
  playerInfo: PlayerInfo;
  playerSeasonStats: PlayerSeasonStats[];
  playerProjections: PlayerProjections;
  transactions: Transaction[];
}

const PlayerDetail: React.FC = () => {
  const { playerName } = useParams<{ playerName: string }>();
  const navigate = useNavigate();
  const [playerDetail, setPlayerDetail] = useState<PlayerDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Effect to fetch player data when component mounts
  useEffect(() => {
    if (playerName) {
      loadPlayerData();
    }
  }, [playerName]);

  const loadPlayerData = async () => {
    if (!playerName) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Decode the URL-encoded player name
      const decodedPlayerName = decodeURIComponent(playerName);
      const data = await fetchPlayerDetail(decodedPlayerName);
      setPlayerDetail(data);
    } catch (err: any) {
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to load player details');
        console.error('Error loading player details:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };


  const capitalizePlayerName = (name: string): string => {
    if (!name) return '';
    
    return name
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Handle Roman numerals (I, II, III, IV, V, etc.)
        if (/^[ivx]+$/i.test(word)) {
          return word.toUpperCase();
        }
        
        // Handle Jr., Sr., etc.
        if (['jr', 'sr', 'jr.', 'sr.'].includes(word.toLowerCase())) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
        
        // Handle names with apostrophes (O'Brien, D'Angelo)
        if (word.includes("'")) {
          return word.split("'").map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          ).join("'");
        }
        
        // Handle hyphenated names (Jean-Claude)
        if (word.includes('-')) {
          return word.split('-').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          ).join('-');
        }
        
        // Regular capitalization
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };



  // Season stats columns configuration
  const seasonStatsColumns: Column[] = [
    {
      key: 'season',
      label: 'Season',
      width: '15%',
      className: 'text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'games',
      label: 'Games',
      width: '10%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'passingYards',
      label: 'Pass Yds',
      width: '12%',
      className: 'text-sm text-text-secondary',
      render: (value) => value?.toLocaleString()
    },
    {
      key: 'passingTDs',
      label: 'Pass TDs',
      width: '12%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'interceptions',
      label: 'INTs',
      width: '10%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'rushingYards',
      label: 'Rush Yds',
      width: '12%',
      className: 'text-sm text-text-secondary',
      render: (value) => value?.toLocaleString()
    },
    {
      key: 'rushingTDs',
      label: 'Rush TDs',
      width: '12%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    },
    {
      key: 'fantasyPoints',
      label: 'Fantasy Pts',
      width: '17%',
      className: 'text-sm font-medium text-text-secondary',
      render: (value) => value
    }
  ];

  return (
    <LayoutWithChat>
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-text-secondary">Loading player details...</div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="text-red-400">{error}</div>
          <button
            onClick={loadPlayerData}
            className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : playerDetail ? (
        <>
          {/* Player Header */}
          <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {playerDetail.playerInfo.headshotUrl && (
                  <img
                    src={playerDetail.playerInfo.headshotUrl}
                    alt={playerDetail.playerInfo.playerName}
                    className="w-24 h-24 rounded-lg object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="flex-grow min-w-0">
                <h1 className="text-3xl font-normal font-primary text-text-primary mb-2">
                  {capitalizePlayerName(playerDetail.playerInfo.playerName) || 'Unknown Player'}
                </h1>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-semibold text-text-primary">
                    {playerDetail.playerInfo.position?.toUpperCase() || 'N/A'}
                  </span>
                  <span className="text-lg text-text-secondary">{playerDetail.playerInfo.team?.toUpperCase() || 'N/A'}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  <div>
                    <p className="text-text-secondary text-sm mb-1">Height</p>
                    <p className="text-text-primary font-medium">{playerDetail.playerInfo.height || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-text-secondary text-sm mb-1">Weight</p>
                    <p className="text-text-primary font-medium">{playerDetail.playerInfo.weight || 'N/A'}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-text-secondary text-sm mb-1">College</p>
                    <p className="text-text-primary font-medium">{playerDetail.playerInfo.college || 'N/A'}</p>
                  </div>
                  <div className="hidden sm:block col-span-3">
                    <p className="text-text-secondary text-sm mb-1">Draft Info</p>
                    <p className="text-text-primary font-medium">{playerDetail.playerInfo.draftInfo || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Season Stats */}
          {playerDetail.playerSeasonStats && playerDetail.playerSeasonStats.length > 0 && (
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-normal font-primary text-text-primary">
                Season Statistics
              </h2>
              <ResponsiveTable
                columns={seasonStatsColumns}
                data={playerDetail.playerSeasonStats?.map(stats => ({
                  season: stats.season.toString(),
                  games: stats.gamesPlayed,
                  passingYards: stats.passingYards || 0,
                  passingTDs: stats.passingTouchdowns || 0,
                  interceptions: stats.interceptions || 0,
                  rushingYards: stats.rushingYards || 0,
                  rushingTDs: stats.rushingTouchdowns || 0,
                  fantasyPoints: stats.fantasyPoints || 0
                })) || []}
                keyExtractor={(stats) => stats.season}
                cardRenderer={(stats) => (
                  <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-text-primary">
                        {stats.season} Season
                      </h3>
                      <div className="text-right">
                        <div className="text-lg font-bold text-text-primary">
                          {stats.fantasyPoints} pts
                        </div>
                        <div className="text-xs text-text-secondary">
                          {stats.games} games
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Passing</div>
                        <div className="text-text-primary">
                          {stats.passingYards?.toLocaleString()} yds, {stats.passingTDs} TDs
                        </div>
                        <div className="text-text-secondary text-xs">
                          {stats.interceptions} INTs
                        </div>
                      </div>
                      <div>
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Rushing</div>
                        <div className="text-text-primary">
                          {stats.rushingYards?.toLocaleString()} yds, {stats.rushingTDs} TDs
                        </div>
                      </div>
                      <div>
                        <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Fantasy</div>
                        <div className="text-text-primary font-medium">
                          {stats.games > 0 ? (stats.fantasyPoints / stats.games).toFixed(1) : '0.0'} PPG
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          )}

          {/* AI Analysis */}
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-normal font-primary text-text-primary">
              AI Analysis
            </h2>
          <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">              <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed space-y-4">
                <p>
                  {playerDetail.playerInfo.playerName} is a key player to watch in fantasy football. 
                  Based on their recent performance and projections, they offer significant value 
                  for fantasy managers.
                </p>
                
                <h3 className="text-lg font-medium text-text-primary mt-6 mb-3">Key Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Position: {playerDetail.playerInfo.position?.toUpperCase() || 'N/A'}</li>
                  <li>Team: {playerDetail.playerInfo.team?.toUpperCase() || 'N/A'}</li>
                  <li>College: {playerDetail.playerInfo.college || 'N/A'}</li>
                  <li>Draft Info: {playerDetail.playerInfo.draftInfo || 'N/A'}</li>
                </ul>

                {playerDetail.playerProjections && (
                  <>
                    <h3 className="text-lg font-medium text-text-primary mt-6 mb-3">2025 Projections</h3>
                    <p>
                      Projected for {playerDetail.playerProjections.games} games with {playerDetail.playerProjections.fantasyPoints} fantasy points.
                    </p>
                  </>
                )}

                <h3 className="text-lg font-medium text-text-primary mt-6 mb-3">Fantasy Recommendation</h3>
                <p>
                  <strong className="text-text-primary">Monitor Closely</strong> - Keep an eye on this player's 
                  performance and matchups for optimal fantasy decisions.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-text-secondary">No player details found</div>
        </div>
      )}
    </LayoutWithChat>
  );
};

export default PlayerDetail;